import React from 'react';
import dayjs from 'dayjs';
import { Box, Stack, Typography } from '@mui/material';
import CheckRounded from '@mui/icons-material/CheckRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { Timeline, ToolbarButton, type TimelineStep } from '../../../../ui-kit';

interface CallsBreakdownProps {
  /** The call list row the calls belong to. */
  row: Record<string, unknown>;
  /** The row's column holding its calls. */
  logKey: string;
  onClose: () => void;
  /** The zone the board's times read in ("CT"); each call's second time is in it. */
  viewerZone?: string;
}

export interface LoggedCall {
  /** Position in the log, newest first; keeps two alike calls apart. */
  n: number;
  date: string;
  weekday: string;
  /** Hour of the day in the client's own time, 0–23. */
  hour: number;
  /** Hour of the day in the zone picked on the board (Eastern unless changed), 0–23. */
  etHour: number;
  seconds: number;
  direction: string;
  agent: string;
  /** What the call stands behind: its window and weekday, its window only, or neither. */
  counts: 'day' | 'time' | '';
}

/**
 * A row's calls as the board stores them: one call to each '^', its fields
 * split by '~' — date, weekday, client's hour, the picked zone's hour, seconds,
 * direction, agent, and what it counts towards.
 */
export function parseCallLog(log: unknown): LoggedCall[] {
  if (typeof log !== 'string' || !log) return [];
  return log.split('^').map((entry, n) => {
    const [date, weekday, hour, etHour, seconds, direction, agent, counts] = entry.split('~');
    return {
      n,
      date,
      weekday,
      hour: Number(hour),
      etHour: Number(etHour),
      seconds: Number(seconds),
      direction: direction ?? '',
      agent: agent ?? '',
      counts: counts === 'day' || counts === 'time' ? counts : '',
    };
  });
}

const clock = (h: number) => `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? 'AM' : 'PM'}`;

/** The hour a call started in, as a range: "3–4 PM". The records keep the hour, not the minute. */
export function hourRange(h: number): string {
  const end = (h + 1) % 24;
  const sameHalf = (h < 12) === (end < 12) && end !== 0;
  return sameHalf ? `${h % 12 === 0 ? 12 : h % 12}–${clock(end)}` : `${clock(h)}–${clock(end)}`;
}

export function callLength(seconds: number): string {
  if (seconds < 60) return `${seconds} s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s ? `${m} min ${s} s` : `${m} min`;
}

const text = (v: unknown) => (v === null || v === undefined || v === '' ? null : String(v));

/**
 * The evidence behind one client's place on the call list, opened under its
 * row the way the Scorecard opens an agent breakdown. Not a table inside the
 * table: the client's 90-second calls as a compact timeline, newest first,
 * one line a call, with a tick on each call the suggested time stands on.
 */
export default function CallsBreakdown({ row, logKey, onClose, viewerZone = 'ET' }: CallsBreakdownProps) {
  const calls = React.useMemo(() => parseCallLog(row[logKey]), [row, logKey]);
  const contact = text(row.hubspot_contact_id) ?? 'this client';
  const zone = text(row.best_time)?.split(' ').pop() ?? null;
  // The second time only when the client's clock is not the viewer's.
  const sameClock = zone === viewerZone;

  const steps: TimelineStep[] = calls.map((c) => ({
    key: String(c.n),
    title: `${dayjs(c.date).format('ddd D MMM YYYY')} · ${hourRange(c.hour)}${zone ? ` ${zone}` : ''}`,
    meta: callLength(c.seconds),
    detail: [
      c.direction,
      c.agent,
      !sameClock && zone ? `${hourRange(c.etHour)} ${viewerZone}` : null,
    ].filter(Boolean).join(' · '),
    tone: c.counts ? 'good' : 'neutral',
    marker: c.counts ? <CheckRounded /> : null,
  }));

  return (
    <Stack spacing={1}>
      {/* The heading and its close sit across the full width, the close at
          the far right; the calls keep to a readable width below. */}
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="subtitle2" sx={{ mr: 'auto' }}>
          Call History · {contact}
        </Typography>
        <ToolbarButton
          variant="ghost"
          icon={<CloseRounded fontSize="small" />}
          onClick={onClose}
          aria-label="Close call history"
          tooltip="Close"
        />
      </Stack>
      {calls.length ? (
        <Box sx={{ maxWidth: 640, maxHeight: 280, overflowY: 'auto', pr: 1 }}>
          <Timeline steps={steps} dense />
        </Box>
      ) : (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
          No call of 90 seconds or more in our call records.
        </Typography>
      )}
    </Stack>
  );
}
